import {
  Box,
  Heading,
  Layer,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
  Button
} from 'grommet'
import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  useMemo
} from 'react'
import GlobalConfig from '../GlobalConfig'
import { I18n } from 'galarm-config'
import { DateTimeUtils, StringUtils } from 'galarm-shared'
import { Close, FormNext, FormPrevious, MoreVertical } from 'grommet-icons'
import { isEmpty, uniqWith } from 'lodash'
import ViewIncidentEscalation from './ViewIncidentEscalation'
import { useDispatch, useSelector } from 'react-redux'
import { doc, getDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import UserContext from './UserContext'
import ActionCreators from '../actions/creators'
import IncidentNotes from './IncidentNotes'

function incidentsReducer(state, action) {
  switch (action.type) {
    case 'add':
      return {
        incidents: uniqWith(
          [action.incident].concat(state.incidents),
          (item1, item2) => item1.id === item2.id
        )
      }
    case 'remove':
      return {
        incidents: state.incidents.filter(
          incident => incident.id !== action.incident.id
        )
      }
    default:
      throw new Error('Unknown action type in incidentsReducer ' + action.type)
  }
}

const ViewIncidentsTable = ({
  incidents,
  columns,
  getMenuItemsForIncident,
  showIncidentMenu,
  getIncidentStateString
}) => (
  <Table>
    <TableHeader>
      <TableRow>
        {columns.map((col, index) => (
          <TableCell key={index} scope="col" border="bottom">
            {col.label}
          </TableCell>
        ))}
        <TableCell scope="col" border="bottom" />
      </TableRow>
    </TableHeader>
    <TableBody>
      {incidents.map((incident, index) => (
        <TableRow key={index}>
          {columns.map((col, colIndex) => (
            <TableCell key={colIndex} scope="row" border="bottom">
              <Text weight={500}>
                {col.render
                  ? col.render(incident)
                  : col.key === 'status'
                  ? getIncidentStateString(incident)
                  : incident[col.key]}
              </Text>
            </TableCell>
          ))}
          {showIncidentMenu && (
            <TableCell border="bottom">
              <Menu
                dropBackground="lightTint"
                icon={<MoreVertical color="textColor" />}
                items={getMenuItemsForIncident(incident)}
              />
            </TableCell>
          )}
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

const ViewIncidents = ({
  showIncidentMenu = true,
  columns,
  incidents,
  header,
  filter = 'none',
  emptyMessage = I18n.t('noIncidents')
}) => {
  const enterpriseUser = useRef(null)

  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10 // Number of incidents per page

  const globalDispatch = useDispatch()

  const { user } = useContext(UserContext)

  const members = useSelector(state => state.accountInfo.members)

  const [state, dispatch] = useReducer(incidentsReducer, { incidents: [] })

  const [showIncidentEscalationDialog, setShowIncidentEscalationDialog] =
    useState({})
  const displayIncidentEscalationDialog = incident =>
    setShowIncidentEscalationDialog({ incident })
  const hideIncidentEscalationDialog = () => setShowIncidentEscalationDialog({})

  const [showIncidentNotesDialog, setShowIncidentNotesDialog] = useState({})
  const displayIncidentNotesDialog = incident =>
    setShowIncidentNotesDialog({ incident })
  const hideIncidentNotesDialog = () => setShowIncidentNotesDialog({})

  useEffect(() => {
    const fetchEnterpriseUser = async () => {
      try {
        const enterpriseUserDoc = doc(
          GlobalConfig.firestoreDb,
          'enterpriseUsers',
          user.uid
        )
        const enterpriseUserSnap = await getDoc(enterpriseUserDoc)
        enterpriseUser.current = enterpriseUserSnap.data()

        if (enterpriseUser.current && !enterpriseUser.current.authToken) {
          const getGalarmApiAuthToken = httpsCallable(
            getFunctions(),
            'getGalarmApiAuthToken'
          )
          const authTokenResult = await getGalarmApiAuthToken()
          enterpriseUser.current = {
            ...enterpriseUser.current,
            authToken: authTokenResult.data.token
          }
        }
      } catch (error) {
        console.error('Error fetching enterprise user:', error)
      }
    }
    fetchEnterpriseUser()
  }, [user])

  useEffect(() => {
    incidents.forEach(incident => {
      if (
        filter === 'none' ||
        (filter === 'acknowledged' && !incident.acknowledgements)
      ) {
        dispatch({ type: 'add', incident })
      } else if (filter === 'acknowledged' && incident.acknowledgements) {
        dispatch({ type: 'remove', incident })
      }
    })
  }, [incidents])

  const getIncidentAcknowledgementString = ack => {
    const { by: acknowledgedBy, at: acknowledgedAt } = ack

    let memberName
    const acknowledgedByMember = members.find(
      item => item.id === acknowledgedBy || item.appUid === acknowledgedBy
    )
    if (acknowledgedByMember) {
      memberName = StringUtils.createDisplayName(
        acknowledgedByMember.firstName,
        acknowledgedByMember.lastName
      )
    } else if (acknowledgedBy === user.uid) {
      memberName = user.displayName
    } else {
      memberName = I18n.t('unknown')
    }

    const dateString = DateTimeUtils.getDateTimeWoYearAsString(acknowledgedAt)
    const acknowledgedString = I18n.t('alertIncidentAcknowledged', {
      memberName,
      dateString
    })

    return acknowledgedString
  }

  const getIncidentClosedString = (closedBy, closedAt) => {
    const dateString = DateTimeUtils.getDateTimeWoYearAsString(closedAt)
    if (closedBy === 'system') {
      return I18n.t('alertIncidentAutoClosed', { dateString })
    }

    let memberName
    const closedByMember = members.find(
      item => item.id === closedBy || item.appUid === closedBy
    )
    if (closedByMember) {
      memberName = StringUtils.createDisplayName(
        closedByMember.firstName,
        closedByMember.lastName
      )
    } else if (closedBy === user.uid) {
      memberName = user.displayName
    } else {
      memberName = I18n.t('unknown')
    }

    const closedString = I18n.t('alertIncidentClosed', {
      memberName,
      dateString
    })

    return closedString
  }

  const getIncidentStateString = incident => {
    // let acknowledgedString, closedString, recoveredString
    let incidentStateString = I18n.t('notAcknowledged')

    const closed = incident.closedBy && incident.closedTimestamp
    const recovered = !!incident.recoveredTimestamp
    const acknowledged = incident.acknowledgements?.length > 0

    if (closed) {
      incidentStateString = getIncidentClosedString(
        incident.closedBy,
        incident.closedTimestamp
      )
    } else if (recovered) {
      const dateString = DateTimeUtils.getDateTimeWoYearAsString(
        incident.recoveredTimestamp
      )
      incidentStateString = I18n.t('alertIncidentRecovered', { dateString })
    } else if (acknowledged) {
      incidentStateString = incident.acknowledgements
        .map(ack => getIncidentAcknowledgementString(ack))
        .join(', ')
    }

    // const incidentStateString = [
    //   acknowledgedString,
    //   recoveredString,
    //   closedString
    // ]
    //   .filter(item => !!item)
    //   .join('\n')

    return incidentStateString
  }

  const sortedIncidents = state.incidents.sort((incident1, incident2) => {
    if (incident1.timestamp < incident2.timestamp) {
      return 1
    } else if (incident1.timestamp > incident2.timestamp) {
      return -1
    } else {
      return 0
    }
  })

  const onCloseIncident = async incident => {
    globalDispatch(
      ActionCreators.closeIncident(incident, enterpriseUser.current)
    )
  }

  const getMenuItemsForIncident = incident => {
    const menuItems = [
      {
        label: I18n.t('viewIncidentNotes'),
        onClick: () => displayIncidentNotesDialog(incident)
      },
      {
        label: I18n.t('viewIncidentEscalation'),
        onClick: () => displayIncidentEscalationDialog(incident)
      }
    ]

    // Only show close incident if it is not already closed
    if (!incident.closedTimestamp) {
      menuItems.push({
        label: I18n.t('closeIncident'),
        onClick: () => onCloseIncident(incident)
      })
    }

    return menuItems
  }

  const isIncidentValid = incident => 'alertId' in incident

  // Get paginated incidents using useMemo
  const paginatedIncidents = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    return sortedIncidents.slice(start, end)
  }, [sortedIncidents, currentPage])

  const totalPages = Math.ceil(sortedIncidents.length / pageSize)

  return (
    <Box align="center">
      <Heading level={4}>{header}</Heading>
      <Box
        overflow="auto"
        background="white"
        pad="medium"
        round="small"
        margin="medium"
        width="large"
        gap="medium">
        {paginatedIncidents.length === 0 && (
          <Box pad="small">
            <Text pad="small">{emptyMessage}</Text>
          </Box>
        )}
        {paginatedIncidents.length > 0 && (
          <Box>
            <ViewIncidentsTable
              incidents={paginatedIncidents}
              columns={columns}
              getMenuItemsForIncident={getMenuItemsForIncident}
              showIncidentMenu={showIncidentMenu}
              getIncidentStateString={getIncidentStateString}
            />
            <Box align="center" width="large" pad={{ top: 'medium' }}>
              <Box direction="row" gap="small" align="center">
                <Button
                  icon={<FormPrevious color="white" />}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  primary
                  plain={false}
                />
                <Text>{`Page ${currentPage} of ${totalPages}`}</Text>
                <Button
                  icon={<FormNext color="white" />}
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage >= totalPages}
                  primary
                  plain={false}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {!isEmpty(showIncidentEscalationDialog) &&
        isIncidentValid(showIncidentEscalationDialog.incident) && (
          <Layer
            background="lightTint"
            onEsc={hideIncidentEscalationDialog}
            onClickOutside={hideIncidentEscalationDialog}>
            <Box margin="medium">
              <ViewIncidentEscalation
                incident={showIncidentEscalationDialog.incident}
                onClose={hideIncidentEscalationDialog}
              />
              <Box style={{ position: 'absolute', top: 20, right: 20 }}>
                <Close onClick={hideIncidentEscalationDialog} />
              </Box>
            </Box>
          </Layer>
        )}
      {!isEmpty(showIncidentNotesDialog) &&
        isIncidentValid(showIncidentNotesDialog.incident) && (
          <Layer
            background="lightTint"
            onEsc={hideIncidentNotesDialog}
            onClickOutside={hideIncidentNotesDialog}>
            <Box margin="medium">
              <IncidentNotes
                incident={showIncidentNotesDialog.incident}
                onClose={hideIncidentNotesDialog}
              />
              <Box style={{ position: 'absolute', top: 20, right: 20 }}>
                <Close onClick={hideIncidentNotesDialog} />
              </Box>
            </Box>
          </Layer>
        )}
    </Box>
  )
}

export default ViewIncidents
